import { yupResolver } from '@hookform/resolvers/yup'
import { find, map } from 'lodash'
import { Info, ListIcon, TagIcon } from 'lucide-react'
import { useCallback, useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useNavigate, useParams } from 'react-router-dom'
import * as yup from 'yup'

import { Header, PageTitle } from '@/components'
import {
  Button,
  Card,
  Checkbox,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
  Input,
  Pill,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui'
import { DateTimePicker } from '@/components/ui/date-time-picker'
import { Table, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { untoldEmails } from '@/core/constants'
import { useAppSelector } from '@/store.ts'
import { getTranslation } from '../../../account/services/auth'
import Loader from '../../../common/components/Loader'
import { Currency, Order } from '../../../common/models'
import {
  OrderPaymentStatus,
  Payments,
  RefundBagTicket,
  RefundOrder,
  TicketCheckin,
} from '../../../common/models/orders'
import FormGroup from '../../../core/components/FormGroup'
import { formatDate, formatDateTime } from '../../../core/constants/times'
import { useCheckPermission } from '../../../core/hooks/use-check-permission'
import { IClient, clientsDetails } from '../../../core/services/clients'
import { getCurrencies } from '../../../core/services/festival-settings'
import {
  alcatrazEnroll,
  backstageIntegration,
  cancelCheckin,
  freeCheckin,
  getOrder,
  getOrderInvoice,
  getRefundOrder,
  getRefundOrderTicket,
  patchTicketOwner,
  payInstallment,
  resetCheckinStatus,
  syncWristband,
} from '../../../core/services/orders'
import { syncWristband as syncWristBandSearchBy } from '../../../core/services/searchBy'
import translate from '../../../core/services/translate'
import { MessageBox } from '../messages/MessageBox'
import { CancelOrderModal } from './CancelOrderModal'
import { CancelTicketModal } from './CancelTicketModal'
import { CheckinAction, CheckinActionModal } from './CheckinActionModal'
import { PayInstallmentModal } from './PayInstallmentModal'
import { Icon } from '@/common/components/Icon'

export interface ILocalGiftedTo {
  id: string
  participationId: string
  phoneNumber: string
  name: string
}

export interface ITicketOwner {
  ticketOwner: {
    id: string
    name: string
    phoneNumber: string
    index: number
  }[]
}

export const OrderDetailsPage: React.FC = () => {
  const language = getTranslation()
  const { festivalName, editionId, orderId } = useParams()
  const navigate = useNavigate()
  const [order, setOrder] = useState<Order>()
  const { hasEditPermission } = useCheckPermission({ page: 'ORDERS' })
  const [selectedTicket, setSelectedTicket] = useState<string>('')
  const [ticketOwnerPhoneNumberEnabled, setTicketOwnerPhoneNumberEnabled] =
    useState(false)

  const [cancelModalShow, setCancelModalShow] = useState(false)
  const [cancelTicketModalShow, setCancelTicketModalShow] = useState(false)
  const [withRuna, setWithRuna] = useState(false)
  const [buyerInfo, setBuyerInfo] = useState<IClient>()
  const [cancelTickets, setCancelTickets] = useState<RefundOrder>()
  const [cancelTicketsIds, setCancelTicketsIds] = useState<string[]>([])
  const [totalRefundAmount, setTotalRefundAmount] = useState<number>(0)
  const [cancelTicket, setCancelTicket] = useState<RefundBagTicket>()
  const [pendingCheckinAction, setPendingCheckinAction] = useState<{
    action: CheckinAction
    checkin: TicketCheckin
  } | null>(null)
  const [pendingPayInstallment, setPendingPayInstallment] =
    useState<Payments | null>(null)
  const [code, setCode] = useState<string>()
  const [isError, setIsError] = useState<boolean>(false)
  const [errorStatus, setErrorStatus] = useState<number | undefined>(undefined)
  const [showMessageBox, setShowMessageBox] = useState<boolean>(false)
  const [message, setMessage] = useState<string>('')
  const [currency, setCurrency] = useState<Currency | null>(null)
  const [submitting, setSubmitting] = useState<boolean>(false)
  const [loading, setLoading] = useState<boolean>(true)

  const getData = useCallback(async () => {
    if (!orderId) return
    setLoading(true)
    try {
      const { status, data } = await getOrder(orderId)
      if (status === 200) {
        setOrder(data)
        setTimeout(() => {
          map(data?.tickets, (ticket, index) => {
            setValueTicketOwner(
              `ticketOwner.${index}.name`,
              ticket.boughtForName,
              { shouldValidate: true },
            )
            setValueTicketOwner(
              `ticketOwner.${index}.phoneNumber`,
              ticket.boughtForPhone,
              { shouldValidate: true },
            )
            setValueTicketOwner(`ticketOwner.${index}.id`, ticket.id, {
              shouldValidate: true,
            })
          })
        }, 300)
        setSelectedTicket(data.tickets?.[0]?.id)
        setCode(data.tickets?.[0]?.code)
      }
      const res = await getRefundOrder(orderId)
      if (res.status === 200) {
        setCancelTickets(res.data)
        for (const bagTicket of res.data.bagTickets) {
          for (const participationTicket of bagTicket.participationTickets) {
            if (participationTicket.refundedAmountDefaultCurrency > 0) {
              setTotalRefundAmount(
                totalRefundAmount +
                  participationTicket.refundedAmountDefaultCurrency,
              )
              cancelTicketsIds.push(
                participationTicket.userParticipationTicketId,
              )
              setCancelTicketsIds(cancelTicketsIds)
            }
            for (const refundExtra of participationTicket.refundExtras) {
              if (refundExtra.refundedAmountDefaultCurrency > 0) {
                setTotalRefundAmount(
                  totalRefundAmount + refundExtra.refundedAmountDefaultCurrency,
                )
              }
            }
            for (const refundCheckins of participationTicket.refundCheckins) {
              if (refundCheckins.refundedAmountDefaultCurrency > 0) {
                setTotalRefundAmount(
                  totalRefundAmount +
                    refundCheckins.refundedAmountDefaultCurrency,
                )
              }
            }
          }
        }
      }

      const client = await clientsDetails(data.buyerId)
      if (client.status === 200) {
        setBuyerInfo(client.data)
      }
    } catch (err) {
      console.log(err)
    } finally {
      setLoading(false)
    }
  }, [orderId])

  useEffect(() => {
    // prettier-ignore
    (async () => {
      try {
        const response = await getCurrencies()
        if (response.status === 200) {
          setCurrency(response.data.defaultCurrency)
        }
      } catch (e) {
        if (e instanceof Error) {
          setIsError(true)
          setShowMessageBox(true)
          setMessage(e.message)
          setErrorStatus((e as { response?: { status?: number } })?.response?.status)
        } else {
          console.error(e)
        }
      }
    })()
  }, [])

  useEffect(() => {
    getData()
  }, [orderId, getData])

  const schemaTicketOwner = yup.object().shape({
    ticketOwner: yup.array().of(
      yup.object().shape({
        id: yup.string().required(),
        name: yup.string().required(),
        phoneNumber: yup.string().required(),
      }),
    ),
  })

  const {
    handleSubmit: handleSubmitTicketOwner,
    register: ticketOwnerRegister,
    formState: {
      errors: ticketOwnerErrors,
      dirtyFields: dirtyFieldsTicketOwner,
    },
    setValue: setValueTicketOwner,
  } = useForm<ITicketOwner>({
    mode: 'onChange',
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    resolver: yupResolver<any>(schemaTicketOwner),
  })

  const submitTickertOwner = async (values: ITicketOwner, index: number) => {
    if (!hasEditPermission) {
      return
    }
    try {
      setSubmitting(true)
      setTicketOwnerPhoneNumberEnabled(false)
      const resp = await patchTicketOwner(values.ticketOwner[index].id, {
        name: values.ticketOwner[index].name,
        phone: values.ticketOwner[index].phoneNumber,
      })
      if (resp.status === 200) {
        getData()
      }
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (e: any) {
      if (e.response.data.status === 400) {
        setIsError(true)
        setShowMessageBox(true)
        setMessage(
          'In order to change the phone number, the check-in must be canceled first.',
        )
      } else {
        if (e instanceof Error) {
          setIsError(true)
          setShowMessageBox(true)
          setMessage(e.message)
          setErrorStatus(
            (e as { response?: { status?: number } })?.response?.status,
          )
        } else {
          console.error(e)
        }
      }
    } finally {
      setSubmitting(false)
    }
  }

  const { user } = useAppSelector((s) => s.app)

  const handleBackstageIntegration = async () => {
    if (!hasEditPermission) {
      return
    }
    try {
      setLoading(true)
      if (!orderId) return
      const resp = await backstageIntegration(orderId)
      if (resp.status === 200) {
        setIsError(false)
        setShowMessageBox(true)
        setMessage('Backstage integration complete!')
      }
    } catch (e) {
      if (e instanceof Error) {
        setIsError(true)
        setShowMessageBox(true)
        setMessage(e.message)
        setErrorStatus(
          (e as { response?: { status?: number } })?.response?.status,
        )
      } else {
        console.error(e)
      }
    } finally {
      setLoading(false)
    }
  }

  const handleSyncWristband = async () => {
    if (!hasEditPermission) {
      return
    }
    try {
      setLoading(true)
      if (!orderId || !code) return
      const resp = await syncWristband(orderId, selectedTicket)
      const resp2 = await syncWristBandSearchBy(code)
      if (resp.status === 200 && resp2.status === 200) {
        setIsError(false)
        setShowMessageBox(true)
        setMessage('Serial Number syncronization complete!')
      }
    } catch (e) {
      if (e instanceof Error) {
        setIsError(true)
        setShowMessageBox(true)
        setMessage(e.message)
        setErrorStatus(
          (e as { response?: { status?: number } })?.response?.status,
        )
      } else {
        console.error(e)
      }
    } finally {
      setLoading(false)
    }
  }

  const handleAlcatrazEnroll = async () => {
    if (!hasEditPermission) {
      return
    }
    try {
      setLoading(true)
      if (!orderId || !selectedTicket) return
      const resp = await alcatrazEnroll(orderId, selectedTicket)
      if (resp.status === 200) {
        setIsError(false)
        setShowMessageBox(true)
        setMessage('Alcatraz enrollment has been triggered.')
      }
    } catch (e) {
      if (e instanceof Error) {
        setIsError(true)
        setShowMessageBox(true)
        setMessage(e.message)
        setErrorStatus(
          (e as { response?: { status?: number } })?.response?.status,
        )
      } else {
        console.error(e)
      }
    } finally {
      setLoading(false)
    }
  }

  const handleConfirmCheckinAction = async () => {
    if (!hasEditPermission || !pendingCheckinAction) {
      return
    }
    const { action, checkin } = pendingCheckinAction
    try {
      setLoading(true)
      const resp =
        action === 'cancel'
          ? await cancelCheckin(checkin.id)
          : await resetCheckinStatus(checkin.id)
      if (resp.status === 200) {
        setIsError(false)
        setShowMessageBox(true)
        setMessage(
          action === 'cancel'
            ? 'The check-in has been cancelled.'
            : 'The check-in status has been reset to NOT_STARTED.',
        )
        const { status, data } = await getOrder(orderId || '')
        if (status === 200) {
          setOrder(data)
        }
      }
    } catch (e) {
      if (e instanceof Error) {
        setIsError(true)
        setShowMessageBox(true)
        setMessage(e.message)
        setErrorStatus(
          (e as { response?: { status?: number } })?.response?.status,
        )
      } else {
        console.error(e)
      }
    } finally {
      setLoading(false)
      setPendingCheckinAction(null)
    }
  }

  const handleConfirmPayInstallment = async () => {
    if (!hasEditPermission || !pendingPayInstallment || !orderId) {
      return
    }
    try {
      setLoading(true)
      const resp = await payInstallment(orderId, pendingPayInstallment.id)
      if (resp.status === 200) {
        setIsError(false)
        setShowMessageBox(true)
        setMessage(
          'Payment triggered — the installment is being processed. Refresh the order shortly to see the result.',
        )
        const { status, data } = await getOrder(orderId)
        if (status === 200) {
          setOrder(data)
        }
      }
    } catch (e) {
      if (e instanceof Error) {
        setIsError(true)
        setShowMessageBox(true)
        setMessage(e.message)
        setErrorStatus(
          (e as { response?: { status?: number } })?.response?.status,
        )
      } else {
        console.error(e)
      }
    } finally {
      setLoading(false)
      setPendingPayInstallment(null)
    }
  }

  const renderCheckinAction = (checkin: TicketCheckin | null) => {
    if (!checkin) {
      return <Pill>No check-in</Pill>
    }

    const cancellable = checkin.status === 'DONE'
    const resettable = checkin.status === 'IN_PROGRESS'

    return (
      <>
        <Pill>Check-in: {checkin.status.replace(/_/g, ' ')}</Pill>
        {cancellable && (
          <Button
            variant="primary-bordered"
            size="sm"
            disabled={!hasEditPermission || checkin.toppedUp}
            title={
              checkin.toppedUp
                ? 'This check-in has been topped up and cannot be cancelled.'
                : undefined
            }
            onClick={() =>
              setPendingCheckinAction({ action: 'cancel', checkin })
            }
          >
            Cancel Check-in
          </Button>
        )}
        {resettable && (
          <Button
            variant="primary-bordered"
            size="sm"
            disabled={!hasEditPermission}
            onClick={() => setPendingCheckinAction({ action: 'reset', checkin })}
          >
            Reset Status
          </Button>
        )}
      </>
    )
  }

  const renderStatus = (status: Payments['status']) => {
    switch (status) {
      case 'PENDING':
        return (
          <span className="flex items-center gap-2 text-accent-4">
            <Icon
              icon="clock"
              width="18"
              height="18"
              viewBox="0 0 18 18"
              fill="none"
            />
            <span>Pending</span>
          </span>
        )
      case 'DEPOSITED':
        return (
          <span className="flex items-center gap-2 text-accent-2">
            <Icon
              icon="check"
              width="18"
              height="18"
              viewBox="0 0 18 18"
              fill="none"
            />
            <span className="text-monochrome-1">Paid</span>
          </span>
        )
      case 'PLANNED':
        return (
          <span className="flex items-center gap-2 text-accent-4">
            <Icon
              icon="clock"
              width="18"
              height="18"
              viewBox="0 0 18 18"
              fill="none"
            />
            <span>Scheduled Payment</span>
          </span>
        )
      case 'FAILED':
        return (
          <span className="flex items-center gap-2 text-accent-3">
            <Icon
              icon="close-circle"
              width="18"
              height="18"
              viewBox="0 0 18 18"
              fill="none"
            />
            <span>Payment Failed</span>
          </span>
        )
      case 'REJECTED':
        return (
          <span className="flex items-center gap-2 text-accent-3">
            <Icon
              icon="close-circle"
              width="18"
              height="18"
              viewBox="0 0 18 18"
              fill="none"
            />
            <span>Payment Rejected</span>
          </span>
        )
      case 'REFUNDED':
        return (
          <span className="flex items-center gap-2 text-cyan-500">
            <Icon
              icon="return"
              width="18"
              height="18"
              viewBox="0 0 18 18"
              fill="none"
            />
            <span>Refunded</span>
          </span>
        )
      case 'CANCELED':
        return (
          <span className="flex items-center gap-2 text-accent-3">
            <Icon
              icon="close-circle"
              width="18"
              height="18"
              viewBox="0 0 18 18"
              fill="none"
            />
            <span>Canceled</span>
          </span>
        )
      case 'REDEEMED':
        return (
          <span className="flex items-center gap-2 text-accent-2">
            <Icon
              icon="check"
              width="18"
              height="18"
              viewBox="0 0 18 18"
              fill="none"
            />
            <span>Redeemed</span>
          </span>
        )
    }
  }

  const handleDownloadInvoice = async () => {
    try {
      setLoading(true)
      if (!orderId) return
      const resp = await getOrderInvoice(orderId)

      if (resp.status === 200) {
        window.open(resp.data, '_blank')
      }
    } catch (e) {
      if (e instanceof Error) {
        setIsError(true)
        setShowMessageBox(true)
        setMessage(e.message)
        setErrorStatus(
          (e as { response?: { status?: number } })?.response?.status,
        )
      } else {
        console.error(e)
      }
    } finally {
      setLoading(false)
    }
  }

  return (
    <>
      <Header
        pageTitle="Order Details"
        actions={
          <div className="flex gap-2 relative">
            {hasEditPermission && !untoldEmails.includes(user?.email ?? '') && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="secondary"
                    size="sm"
                    className="border border-monochrome-120 data-[state=open]:bg-gradient-6"
                  >
                    Cancel
                    <Icon
                      icon="arrow-down"
                      width="14"
                      height="14"
                      viewBox="0 0 24 24"
                      fill="none"
                      className="text-white shrink-0"
                    />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="center" className="w-[320px]">
                  <DropdownMenuItem
                    onSelect={(e) => {
                      e.preventDefault()
                      setCancelModalShow(true)
                      setWithRuna(true)
                    }}
                  >
                    Cancel & refund with RUNA
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onSelect={(e) => {
                      e.preventDefault()
                      setCancelModalShow(true)
                      setWithRuna(false)
                    }}
                  >
                    Cancel without RUNA
                  </DropdownMenuItem>
                  {/*{!!cancelTickets?.bagTickets &&*/}
                  {/*  cancelTickets?.bagTickets?.length > 1 && (*/}
                  {/*    <>*/}
                  {/*      <DropdownMenuSeparator />*/}
                  {/*      <DropdownMenuSub>*/}
                  {/*        <DropdownMenuSubTrigger>*/}
                  {/*          Cancel partial*/}
                  {/*        </DropdownMenuSubTrigger>*/}
                  {/*        <DropdownMenuSubContent className="w-[320px]">*/}
                  {/*          {map(cancelTickets?.bagTickets, (ticket, index) => (*/}
                  {/*            <div key={`user-bag-ticket-${index}`}>*/}
                  {/*              <DropdownMenuLabel className="px-3 py-2 text-xs text-monochrome-2">*/}
                  {/*                {ticket.participationTickets.length > 1*/}
                  {/*                  ? ticket.bagTicketId*/}
                  {/*                  : ticket.participationTickets[0]*/}
                  {/*                      .ticketCode}{' '}*/}
                  {/*                -{' '}*/}
                  {/*                {ticket.participationTickets[0].boughtForName}*/}
                  {/*              </DropdownMenuLabel>*/}
                  {/*              <DropdownMenuItem*/}
                  {/*                onSelect={async (e) => {*/}
                  {/*                  if (orderId) {*/}
                  {/*                    const res = await getRefundOrderTicket(*/}
                  {/*                      orderId,*/}
                  {/*                      ticket.participationTickets[0]*/}
                  {/*                        .userParticipationTicketId,*/}
                  {/*                    )*/}
                  {/*                    setCancelTicket(res.data)*/}
                  {/*                  }*/}
                  {/*                  e.preventDefault()*/}
                  {/*                  setCancelTicketModalShow(true)*/}
                  {/*                  setWithRuna(true)*/}
                  {/*                }}*/}
                  {/*              >*/}
                  {/*                Cancel & refund with RUNA*/}
                  {/*              </DropdownMenuItem>*/}
                  {/*              <DropdownMenuItem*/}
                  {/*                onSelect={async (e) => {*/}
                  {/*                  if (orderId) {*/}
                  {/*                    const res = await getRefundOrderTicket(*/}
                  {/*                      orderId,*/}
                  {/*                      ticket.bagTicketId,*/}
                  {/*                    )*/}
                  {/*                    setCancelTicket(res.data)*/}
                  {/*                  }*/}
                  {/*                  e.preventDefault()*/}
                  {/*                  setCancelTicketModalShow(true)*/}
                  {/*                  setWithRuna(false)*/}
                  {/*                }}*/}
                  {/*              >*/}
                  {/*                Cancel without RUNA*/}
                  {/*              </DropdownMenuItem>*/}
                  {/*              {index <*/}
                  {/*                cancelTickets.bagTickets.length - 1 && (*/}
                  {/*                <DropdownMenuSeparator />*/}
                  {/*              )}*/}
                  {/*            </div>*/}
                  {/*          ))}*/}
                  {/*        </DropdownMenuSubContent>*/}
                  {/*      </DropdownMenuSub>*/}
                  {/*    </>*/}
                  {/*  )}*/}
                </DropdownMenuContent>
              </DropdownMenu>
            )}
            {hasEditPermission && (
              <Button
                size="sm"
                variant="primary-bordered"
                onClick={() => handleBackstageIntegration()}
              >
                Start Backstage Integration
                <Icon
                  icon="export-2"
                  width="18"
                  height="18"
                  viewBox="0 0 18 18"
                  fill="none"
                  className="text-monochrome-5 shrink-0"
                />
              </Button>
            )}

            {hasEditPermission && (
              <Button size="sm">
                Send Check-in
                <Icon
                  icon="export-2"
                  width="18"
                  height="18"
                  viewBox="0 0 18 18"
                  fill="none"
                  className="text-monochrome-5 shrink-0"
                />
              </Button>
            )}
          </div>
        }
      />
      {(loading || submitting) && <Loader />}
      <div className="flex items-center justify-between gap-4 mb-6">
        <PageTitle
          title="Order Details"
          subtitle={order?.id}
          icon="bag-2"
          backButtonFunction={() => navigate(-1)}
          backButton={true}
        />

        <Button variant="secondary" size="sm" onClick={handleDownloadInvoice}>
          Download Invoice
          <Icon
            icon="document-text"
            width="18"
            height="18"
            viewBox="0 0 18 18"
            fill="none"
            className="shrink-0"
          />
        </Button>
      </div>
      <div className="overflow-y-auto max-h-[calc(100svh-190px)]">
        <Card className="bg-monochrome-4-60 p-[30px]">
          <div className="flex gap-2 flex-col">
            <div className="flex gap-2 items-center mb-2">
              <span className="text-body-large font-bold">Order Info</span>
              {/* {!!order?.paymentStatus && getIcon(order.paymentStatus)}
            {order?.paymentStatus} */}
              {
                //TODO when we have api integration we should get the message for failed status as well
              }
            </div>
            {totalRefundAmount > 0 && (
              <Pill>
                {totalRefundAmount < (order?.price.value ?? 0)
                  ? 'This order has been partially refunded'
                  : 'This order has been fully refunded'}
              </Pill>
            )}
            <div className="gap-3 grid grid-cols-3">
              <FormGroup label="Order ID">
                <Input
                  className="h-[40px]"
                  type="text"
                  disabled
                  value={order?.id}
                />
              </FormGroup>
              {!!order?.purchaseDate && (
                <FormGroup label="Purchase Date">
                  <Input
                    className="h-[40px]"
                    type="text"
                    disabled
                    value={formatDateTime(order?.purchaseDate)}
                  />
                </FormGroup>
              )}
              <FormGroup label="Order Price">
                <Input
                  className="h-[40px]"
                  type="text"
                  disabled
                  value={`${order?.price.value} ${currency?.symbol} + RUNA ${order?.price.runa}`}
                />
              </FormGroup>
              <FormGroup label="Bonus Received">
                <Input
                  className="h-[40px]"
                  type="text"
                  disabled
                  value={`${order?.bonus} RUNA`}
                />
              </FormGroup>
              <FormGroup
                label="Bonus Availability Until Date"
                labelProps={{ style: { width: 'auto' } }}
              >
                <Input
                  className="h-[40px]"
                  type="text"
                  disabled
                  value={
                    order?.bonusAvailableUntil
                      ? formatDateTime(order?.bonusAvailableUntil)
                      : ''
                  }
                />
              </FormGroup>

              <FormGroup label="From">
                <Input
                  className="h-[40px]"
                  type="text"
                  disabled
                  value={order?.agent}
                />
              </FormGroup>
            </div>
          </div>
        </Card>
        <Card className="bg-monochrome-4-60 p-[30px] mt-5">
          <div className="flex gap-2 items-center justify-between">
            <div className="flex gap-2 items-center">
              {order?.buyerId && (
                <div className="flex gap-2 py-3 items-center">
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger>
                        <Icon
                          icon="info-2"
                          width="18"
                          height="18"
                          viewBox="0 0 18 18"
                          fill="none"
                          className="hover:text-accent-1"
                        />
                      </TooltipTrigger>
                      <TooltipContent side="right" className="flex flex-col">
                        <span className="text-monochrome-1 text-button-m">
                          {!!buyerInfo?.firstName && !!buyerInfo?.lastName
                            ? `${buyerInfo?.firstName} ${buyerInfo?.lastName}`
                            : order.buyerBillingAddress.name}
                        </span>
                        <span className="text-monochrome-1 text-button-m">
                          {buyerInfo?.email ?? order.buyerBillingAddress.email}
                        </span>
                        <span className="text-monochrome-1 text-button-m">
                          {buyerInfo?.phoneNumber ??
                            order.buyerBillingAddress.phoneNumber}
                        </span>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                  <h3 className="mb-0">Buyer Info</h3>
                </div>
              )}
            </div>
            {!!buyerInfo?.firstName &&
              !!buyerInfo?.lastName &&
              hasEditPermission &&
              order?.buyerId && (
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() =>
                    navigate(
                      `/clients/${festivalName}/${editionId}/${order?.buyerId}`,
                    )
                  }
                >
                  Edit User Details
                  <Icon
                    icon="edit-2"
                    width="18"
                    height="18"
                    viewBox="0 0 18 18"
                    fill="none"
                    className="shrink-0"
                  />
                </Button>
              )}
          </div>
        </Card>
        <Card className="bg-monochrome-4-60 p-[30px] mt-5">
          <div className="flex gap-2 items-center">
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger>
                  <Icon
                    icon="info-2"
                    width="18"
                    height="18"
                    viewBox="0 0 18 18"
                    fill="none"
                    className="hover:text-accent-1"
                  />
                </TooltipTrigger>
                <TooltipContent side="right" className="flex flex-col">
                  <span className="text-monochrome-1 text-button-m">
                    {order?.buyerBillingAddress.name}
                  </span>
                  <span className="text-monochrome-1 text-button-m">
                    {order?.buyerBillingAddress.address}
                  </span>
                  <span className="text-monochrome-1 text-button-m">
                    {order?.buyerBillingAddress.city},{' '}
                    {order?.buyerBillingAddress.country}
                  </span>
                  <span className="text-monochrome-1 text-button-m">
                    {order?.buyerBillingAddress.postCode}
                  </span>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
            <h3 className="mb-0">Buyer Billing Address</h3>
          </div>
        </Card>
        {order?.payments && order?.payments.length > 0 && (
          <Card className="bg-monochrome-4-60 p-[30px] mt-5">
            <div className="flex gap-2 flex-col">
              <h3 className="text-subtitle-m">Manage Monthly Payments</h3>
              {map(order.payments, (payment, index) => {
                const date = formatDate(payment.date)
                return (
                  <Card
                    key={payment.id}
                    className="flex flex-col p-[30px] bg-monochrome-120 border-none"
                  >
                    <div className="flex gap-3 flex-col">
                      <h4 className="text-monochrome-110">Rate {index + 1}</h4>
                      <div className="grid grid-cols-4 gap-2.5">
                        <FormGroup label="Date">
                          <Input
                            className="h-[40px]"
                            disabled
                            type="text"
                            value={date}
                          />
                        </FormGroup>
                        <FormGroup label="Price">
                          <Input
                            className="h-[40px]"
                            disabled
                            type="text"
                            value={payment.price}
                          />
                        </FormGroup>
                        <FormGroup label="Payment Status">
                          <div className="w-full h-[40px] flex items-center cursor-not-allowed opacity-50 rounded-[16px] border !border-monochrome-120 !bg-monochrome-3-60 !text-monochrome-1 px-3 py-2 text-button-m">
                            {renderStatus(payment.status)}
                          </div>
                        </FormGroup>

                        {hasEditPermission &&
                          ['PLANNED', 'FAILED', 'REJECTED'].includes(
                            payment.status,
                          ) && (
                            <Button
                              variant="secondary"
                              size="sm"
                              disabled={loading}
                              onClick={() => setPendingPayInstallment(payment)}
                            >
                              Pay Now
                            </Button>
                          )}
                      </div>
                    </div>
                    {hasEditPermission && payment.status !== 'DEPOSITED' && (
                      <div className="flex justify-between pt-3 border-t border-monochrome-120 mt-2">
                        <Button variant="destructive" size="sm">
                          Delete
                        </Button>
                        <div className="flex gap-2">
                          <Button variant="secondary" size="sm">
                            Cancel
                          </Button>
                          <Button disabled={submitting} type="submit" size="sm">
                            Save
                          </Button>
                        </div>
                      </div>
                    )}
                  </Card>
                )
              })}
              {hasEditPermission && (
                <div className="flex justify-center">
                  <Button variant="secondary" className="mt-4 w-full" size="sm">
                    Add New Rate
                  </Button>
                </div>
              )}
            </div>
          </Card>
        )}
        <div className="flex gap-2 py-3 flex-col">
          {order?.tickets && order?.tickets.length > 0 && (
            <Tabs
              value={selectedTicket}
              onValueChange={(value) => {
                setSelectedTicket(value)
                const ticket = order?.tickets?.find((t) => t.id === value)
                setCode(ticket?.code)
              }}
              className="w-full"
            >
              <div className="overflow-x-auto pt-2">
                <TabsList className="grid w-full mb-6">
                  <div className="bg-white/10 p-2.5 rounded-[20px]">
                    {map(order?.tickets, (ticketInfo) => {
                      const tabName = `${
                        ticketInfo.code ? `${ticketInfo.code} - ` : ''
                      }${ticketInfo.boughtForName}`
                      return (
                        <TabsTrigger key={ticketInfo.id} value={ticketInfo.id}>
                          {tabName}
                        </TabsTrigger>
                      )
                    })}
                  </div>
                </TabsList>
              </div>
              {map(order?.tickets, (ticketInfo, index) => {
                return (
                  <TabsContent key={ticketInfo.id} value={ticketInfo.id}>
                    <Card className="flex gap-3 flex-col p-[30px] bg-monochrome-4-60">
                      <h3 className="text-subtitle-m">Ticket Info</h3>
                      {cancelTicketsIds.includes(ticketInfo.id) && (
                        <Pill>This ticket has been refunded</Pill>
                      )}
                      <div className="grid grid-cols-4 gap-2.5">
                        <FormGroup label="Ticket Name">
                          <Input disabled type="text" value={ticketInfo.name} />
                        </FormGroup>
                        <FormGroup
                          label="Category"
                          tooltip={translate(
                            language.value,
                            'orders.details.ticketInfo.categoryTooltip',
                          )}
                        >
                          <div className="flex items-center gap-2 w-full">
                            <Input
                              type="text"
                              disabled
                              value={ticketInfo.category}
                            />
                          </div>
                        </FormGroup>
                        <FormGroup
                          label={`Ticket Price in ${currency?.symbol}`}
                          tooltip={translate(
                            language.value,
                            'orders.details.ticketInfo.ticketPriceInfoTooltip',
                          )}
                          labelProps={{
                            style: { width: 'auto' },
                          }}
                        >
                          <div className="flex items-center gap-2 w-full">
                            <Input
                              type="text"
                              disabled
                              value={ticketInfo.price}
                            />
                          </div>
                        </FormGroup>
                        <FormGroup
                          label="Voucher Applied"
                          tooltip={translate(
                            language.value,
                            'orders.details.ticketInfo.voucherAppliedTooltip',
                          )}
                          labelProps={{
                            style: { width: 'auto' },
                          }}
                        >
                          <div className="flex items-center gap-2 w-full">
                            <Input
                              type="text"
                              disabled
                              value={ticketInfo?.voucher || 0}
                            />
                          </div>
                        </FormGroup>
                      </div>
                      <div className="flex justify-between items-center mt-[30px]">
                        <FormGroup>
                          <div className="flex items-center gap-2">
                            <Checkbox
                              checked={ticketInfo.freeCheckin}
                              onCheckedChange={async () => {
                                if (!hasEditPermission) {
                                  return
                                }
                                try {
                                  setLoading(true)
                                  const res = await freeCheckin(
                                    order.id,
                                    ticketInfo.id,
                                    ticketInfo.freeCheckin
                                      ? '?freeCheckin=false'
                                      : '?freeCheckin=true',
                                  )
                                  if (res.status === 200) {
                                    const { status, data } = await getOrder(
                                      orderId || '',
                                    )
                                    if (status === 200) {
                                      setOrder(data)
                                    }
                                  }
                                } catch (err) {
                                  console.log(err)
                                } finally {
                                  setLoading(false)
                                }
                              }}
                            />
                            <span>Free Checkin</span>
                          </div>
                        </FormGroup>
                        <div className="flex items-center gap-2">
                          {renderCheckinAction(ticketInfo.checkin)}
                          {ticketInfo.alcatrazIntegratable && (
                            <>
                              {ticketInfo.alcatrazEnrollmentCompleted && (
                                <Pill>Enrolled</Pill>
                              )}
                              <Button
                                variant="primary-bordered"
                                onClick={handleAlcatrazEnroll}
                                size="sm"
                              >
                                {ticketInfo.alcatrazEnrollmentCompleted
                                  ? 'Re-enroll in Alcatraz'
                                  : 'Enroll in Alcatraz'}
                              </Button>
                            </>
                          )}
                          {!!code && (
                            <Button
                              variant="primary-bordered"
                              onClick={handleSyncWristband}
                              size="sm"
                            >
                              Sync Serial Number
                              <Icon
                                icon="refresh-square-2"
                                width="18"
                                height="18"
                                viewBox="0 0 18 18"
                                fill="none"
                              />
                            </Button>
                          )}
                        </div>
                      </div>
                    </Card>

                    {ticketInfo.invite && (
                      <Card className="mt-[30px] flex gap-3 flex-col p-[30px] bg-monochrome-4-60">
                        <h3 className="text-subtitle-m">Invite Info</h3>
                        <div className="grid grid-cols-4 gap-2.5">
                          <FormGroup label="Redeem Code">
                            <Input
                              disabled
                              type="text"
                              value={ticketInfo.invite.redeemCode || ''}
                            />
                          </FormGroup>
                          <FormGroup label="Batch Name">
                            <Input
                              disabled
                              type="text"
                              value={ticketInfo.invite.batchName || ''}
                            />
                          </FormGroup>
                          <FormGroup label="For Email">
                            <Input
                              disabled
                              type="text"
                              value={ticketInfo.invite.forEmail || ''}
                            />
                          </FormGroup>
                          <FormGroup label="Used Date">
                            <Input
                              disabled
                              type="text"
                              value={
                                ticketInfo.invite.usedDate
                                  ? formatDateTime(ticketInfo.invite.usedDate)
                                  : ''
                              }
                            />
                          </FormGroup>
                        </div>
                      </Card>
                    )}

                    <Card className="mt-[30px] p-[30px] bg-monochrome-4-60">
                      <h3 className="mb-4">Owner Ticket Info</h3>
                      <form
                        id={`ticket-owner-form-${index}`}
                        className="flex gap-3 flex-col"
                        onSubmit={handleSubmitTicketOwner((values) =>
                          submitTickertOwner(values, index),
                        )}
                      >
                        <div className="flex gap-3 items-center">
                          <FormGroup label="First & Last Name">
                            <Input
                              isInvalid={
                                !!ticketOwnerErrors?.ticketOwner?.[index]?.name
                                  ?.message
                              }
                              type="text"
                              className="h-[40px]"
                              {...ticketOwnerRegister(
                                `ticketOwner.${index}.name`,
                              )}
                            />
                          </FormGroup>
                          <FormGroup label="Phone Number">
                            <div className="flex gap-2 w-full">
                              <Input
                                isInvalid={
                                  !!ticketOwnerErrors?.ticketOwner?.[index]
                                    ?.phoneNumber?.message
                                }
                                {...ticketOwnerRegister(
                                  `ticketOwner.${index}.phoneNumber`,
                                )}
                                type="text"
                                className="w-full h-[40px]"
                                disabled={!ticketOwnerPhoneNumberEnabled}
                              />
                              <Button
                                style={{ minWidth: '200px' }}
                                variant="secondary"
                                disabled={!hasEditPermission}
                                size="sm"
                                type="button"
                                onClick={() =>
                                  setTicketOwnerPhoneNumberEnabled(
                                    !ticketOwnerPhoneNumberEnabled,
                                  )
                                }
                              >
                                Change Number
                              </Button>
                            </div>
                          </FormGroup>
                        </div>

                        {hasEditPermission && (
                          <div className="flex gap-2 justify-end border-t border-monochrome-120 pt-4 mt-4">
                            <Button variant="secondary">Cancel</Button>
                            <Button
                              disabled={
                                submitting ||
                                Object.keys(dirtyFieldsTicketOwner).length === 0
                              }
                              form={`ticket-owner-form-${index}`}
                              type="submit"
                              loading={submitting}
                            >
                              Save
                            </Button>
                          </div>
                        )}
                      </form>
                    </Card>
                    <Card className="mt-[30px] p-[30px] bg-monochrome-4-60">
                      <div className="flex gap-2 flex-col mb-2">
                        <h3 className="mb-0">Add-ons</h3>
                        <Card className="flex gap-3 flex-col p-4 bg-monochrome-120">
                          <FormGroup>
                            <div className="flex items-center gap-2">
                              <Checkbox disabled checked={false} />
                              <span className="fw-bold">Risk Free</span>
                            </div>
                          </FormGroup>
                          <FormGroup label="Price">
                            <Input
                              type="text"
                              disabled
                              value={
                                find(
                                  ticketInfo.addons,
                                  (addon) => addon.name === 'Risk Free',
                                )?.price
                              }
                            />
                          </FormGroup>
                        </Card>
                        <Card className="flex gap-3 flex-col p-3 bg-monochrome-120 mt-2.5">
                          <FormGroup>
                            <div className="flex items-center gap-2">
                              <Checkbox disabled checked={false} />
                              <span className="font-bold">Wristband</span>
                            </div>
                          </FormGroup>
                          <div className="grid gap-3 grid-cols-5 mb-4 bg-monochrome-3-60 border border-monochrome-120 rounded-[16px] p-3">
                            <div className="flex flex-col">
                              <p>Shipping Address</p>
                            </div>
                            <div className="flex flex-col">
                              <p>Order Date</p>
                            </div>
                            <div className="flex flex-col">
                              <p>Status</p>
                            </div>
                            <div className="flex flex-col">
                              <p>AWB Code</p>
                            </div>

                            <div className="flex flex-col">
                              <p>View</p>
                            </div>
                          </div>
                          <FormGroup label="Price">
                            <Input
                              type="text"
                              disabled
                              value={
                                find(
                                  ticketInfo.addons,
                                  (addon) => addon.name === 'Wristband',
                                )?.price
                              }
                            />
                          </FormGroup>
                        </Card>
                      </div>
                    </Card>
                    {ticketInfo.giftedTo && (
                      <Card className="mt-[30px] p-[30px] bg-monochrome-4-60">
                        <div className="flex gap-4">
                          <div className="flex gap-2 flex-col flex-grow-1 flex-shrink-1">
                            <h3 className="mb-2">Gifted to</h3>
                            <Card className="flex gap-3 flex-col p-4">
                              <FormGroup label="ID">
                                <Input
                                  type="text"
                                  disabled
                                  value={ticketInfo.giftedTo.id}
                                />
                              </FormGroup>
                              <FormGroup label="Name">
                                <Input
                                  type="text"
                                  disabled
                                  value={ticketInfo.giftedTo.name}
                                />
                              </FormGroup>
                              <FormGroup label="Phone Number">
                                <Input
                                  type="text"
                                  disabled
                                  value={ticketInfo.giftedTo.phoneNumber}
                                />
                              </FormGroup>
                              <FormGroup label="Referral Code">
                                <Input
                                  type="text"
                                  disabled
                                  value={ticketInfo.giftedTo.referralCode}
                                />
                              </FormGroup>
                              <FormGroup label="Created Date">
                                <Input
                                  type="text"
                                  disabled
                                  value={formatDateTime(
                                    ticketInfo.giftedTo.createdDate,
                                  )}
                                />
                              </FormGroup>
                              <FormGroup label="Last Activity time">
                                <Input
                                  type="text"
                                  disabled
                                  value={formatDateTime(
                                    ticketInfo.giftedTo.lastActivityTime,
                                  )}
                                />
                              </FormGroup>
                              <FormGroup
                                label="Number of Orders"
                                labelProps={{ style: { width: 'auto' } }}
                              >
                                <Input
                                  type="text"
                                  disabled
                                  value={ticketInfo.giftedTo.ordersSize}
                                />
                              </FormGroup>
                              <FormGroup label="User Location">
                                <Input
                                  type="text"
                                  disabled
                                  value={ticketInfo.giftedTo.location}
                                />
                              </FormGroup>
                              {hasEditPermission && (
                                <div className="flex justify-end">
                                  <Button type="button" variant="secondary">
                                    Edit User Details
                                  </Button>
                                </div>
                              )}
                            </Card>
                          </div>
                        </div>
                        {/*{hasEditPermission &&*/}
                        {/*  !untoldEmails.includes(user?.email ?? '') && (*/}
                        {/*    <div className="flex">*/}
                        {/*      <Button*/}
                        {/*        variant="destructive"*/}
                        {/*        className="w-full"*/}
                        {/*        onClick={async () => {*/}
                        {/*          if (orderId) {*/}
                        {/*            const res = await getRefundOrderTicket(*/}
                        {/*              orderId,*/}
                        {/*              ticketInfo.id,*/}
                        {/*            )*/}
                        {/*            setCancelTicket(res.data)*/}
                        {/*          }*/}
                        {/*          setCancelModalShow(true)*/}
                        {/*        }}*/}
                        {/*      >*/}
                        {/*        Refund Ticket*/}
                        {/*      </Button>*/}
                        {/*    </div>*/}
                        {/*  )}*/}
                      </Card>
                    )}
                  </TabsContent>
                )
              })}
            </Tabs>
          )}
        </div>
      </div>
      <CancelOrderModal
        showCancelModal={cancelModalShow}
        setShowCancelModal={setCancelModalShow}
        order={cancelTickets}
        withRuna={withRuna}
        orderId={order?.id || ''}
        currency={currency?.symbol || 'RON'}
      />
      <CancelTicketModal
        showCancelModal={cancelTicketModalShow}
        setShowCancelModal={setCancelTicketModalShow}
        ticket={cancelTicket}
        withRuna={withRuna}
        orderId={order?.id || ''}
        currency={currency?.symbol || 'RON'}
      />
      <CheckinActionModal
        open={!!pendingCheckinAction}
        action={pendingCheckinAction?.action || 'reset'}
        loading={loading}
        onOpenChange={(open) => !open && setPendingCheckinAction(null)}
        onConfirm={handleConfirmCheckinAction}
      />
      <PayInstallmentModal
        open={!!pendingPayInstallment}
        installment={pendingPayInstallment}
        loading={loading}
        onOpenChange={(open) => !open && setPendingPayInstallment(null)}
        onConfirm={handleConfirmPayInstallment}
      />
      <MessageBox
        message={message}
        setShowMessageBox={setShowMessageBox}
        showMessageBox={showMessageBox}
        isError={isError}
        errorStatus={errorStatus}
      />
    </>
  )
}
